import endpoints from './endpoints.js'
import httpClient from './httpClient'

// login
const login = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.LOGIN, payload)
}

// login with security code
const loginWithSecurityCode = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.LOGIN_WITH_SECURITY_CODE, payload)
}

// get customer info
const getCustomerInfo = async () => {
  return await httpClient.get(endpoints.CUSTOMERS.INFO)
}

// create account
const createAccount = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.REGISTER, payload)
}

// reset password
const resetPassword = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.RESET_PASSWORD, payload)
}

// send verification code
const sendVerificationCode = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.SEND_VERIFICATION_CODE, payload)
}

// send verification code for email
const sendVerificationCodeEmail = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.SEND_VERIFICATION_CODE_EMAIL, payload)
}

// verify phone
const verifyPhone = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.VERIFY_PHONE, payload)
}

// verify email
const verifyEmail = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.VERIFY_EMAIL, payload)
}
// update customer
const updateCustomer = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.UPDATE_CUSTOMER.replace(':customerId', customerId), payload)
}

// get addresses
const getAddresses = async (customerId, payload) => {
  return await httpClient.get(endpoints.CUSTOMERS.ADDRESSES.replace(':customerId', customerId), {
    params: payload || undefined,
  })
}

// get single address
const getSingleAddress = async (customerId, payload) => {
  return await httpClient.get(endpoints.CUSTOMERS.ADDRESSES.replace(':customerId', customerId), {
    params: payload || undefined,
  })
}

// get favorite items
const getFavoriteItems = async (customerId) => {
  return await httpClient.get(endpoints.CUSTOMERS.FAVORITE_ITEMS.replace(':customerId', customerId))
}

// add favorite item
const addFavoriteItem = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.ADD_FAVORITE_ITEM.replace(':customerId', customerId), payload)
}

// remove favorite item
const removeFavoriteItem = async (payload, customerId) => {
  return await httpClient.delete(endpoints.CUSTOMERS.REMOVE_FAVORITE_ITEM.replace(':customerId', customerId), {
    params: payload || undefined,
  })
}

// get saved cards
const getSavedCards = async (customerId, payload) => {
  return await httpClient.get(endpoints.CUSTOMERS.SAVED_CARDS.replace(':customerId', customerId), {
    params: payload || undefined,
  })
}

// get loyalty info
const getLoyaltyInfo = async (customerId) => {
  return await httpClient.get(endpoints.CUSTOMERS.LOYALTY_INFO.replace(':customerId', customerId))
}

// get orders
const getOrders = async (customerId, options = {}) => {
  // Use explicit query building to support repeated status_id parameters:
  // e.g. status_id=11&status_id=20 (NOT status_id[]=11&status_id[]=20)
  const qs = new URLSearchParams()
  qs.set('include_items', 'true')

  if (options.all_orders !== undefined) qs.set('all_orders', String(options.all_orders))
  if (options.search) qs.set('search', String(options.search))
  if (options.sort) qs.set('sort', String(options.sort))
  if (options.page) qs.set('page', String(options.page))
  if (options.limit) qs.set('limit', String(options.limit))

  if (Array.isArray(options.status_ids) && options.status_ids.length > 0) {
    options.status_ids.forEach((id) => {
      if (id !== undefined && id !== null) qs.append('status_id', String(id))
    })
  } else if (options.status_id !== undefined && options.status_id !== null) {
    qs.append('status_id', String(options.status_id))
  }

  const base = endpoints.CUSTOMERS.ORDERS.replace(':customerId', customerId)
  const endpoint = qs.toString() ? `${base}?${qs.toString()}` : base
  return await httpClient.get(endpoint)
}

// save address
const saveAddress = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.SAVE_ADDRESS.replace(':customerId', customerId), payload)
}

// update address
const updateAddress = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.UPDATE_ADDRESS.replace(':customerId', customerId), payload)
}

// delete address
const deleteAddress = async (payload, customerId) => {
  return await httpClient.delete(endpoints.CUSTOMERS.DELETE_ADDRESS.replace(':customerId', customerId), {
    params: payload || undefined,
  })
}

// save card
const saveCard = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.SAVE_CARD.replace(':customerId', customerId), payload)
}

// delete card
const deleteCard = async (payload, customerId) => {
  return await httpClient.delete(endpoints.CUSTOMERS.DELETE_CARD.replace(':customerId', customerId), {
    params: payload || undefined,
  })
}

// change password
const changePassword = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.CHANGE_PASSWORD, payload)
}

// delete customer
const deleteCustomer = async (customerId) => {
  return await httpClient.delete(endpoints.CUSTOMERS.DELETE_CUSTOMER.replace(':customerId', customerId))
}

// add favorite store
const addFavoriteStore = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.ADD_FAVORITE_STORE.replace(':customerId', customerId), payload)
}

// remove favorite store
const removeFavoriteStore = async (payload, customerId) => {
  return await httpClient.delete(endpoints.CUSTOMERS.REMOVE_FAVORITE_STORE.replace(':customerId', customerId), {
    data: payload,
  })
}

// get favorite stores
const getFavoriteStores = async (customerId) => {
  return await httpClient.get(endpoints.CUSTOMERS.FAVORITE_STORES.replace(':customerId', customerId))
}

// guest login
const guestLogin = async () => {
  return await httpClient.post(endpoints.CUSTOMERS.GUEST_LOGIN)
}

// update guest customer info
const updateGuestCustomerInfo = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.UPDATE_GUEST_INFO.replace(':customerId', customerId), payload)
}

// logout
const logout = async () => {
  return await httpClient.post(endpoints.CUSTOMERS.LOGOUT)
}

// redeem loyalty in store
const redeemLoyaltyInStore = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.REDEEM_IN_STORE.replace(':customerId', customerId), payload)
}

// collect loyalty in store
const collectLoyaltyInStore = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.COLLECT_IN_STORE, payload)
}

// foreign auth
const foreignAuth = async (payload) => {
  return await httpClient.post(endpoints.CUSTOMERS.FOREIGN_AUTH, payload)
}

// get gift cards
const getGiftCards = async (payload, customerId) => {
  return await httpClient.get(endpoints.CUSTOMERS.SAVED_CARDS.replace(':customerId', customerId), {
    params: payload || undefined,
  })
}

// checkin for an order
const checkinForOrder = async (payload, customerId, orderId) => {
  return await httpClient.post(
    endpoints.CUSTOMERS.CHECKIN_FOR_ORDER.replace(':customerId', customerId).replace(':orderId', orderId),
    payload
  )
}

// get orders available for checkin
const getOrdersAvailableForCheckin = async (customerId) => {
  return await httpClient.get(endpoints.CUSTOMERS.ORDERS_AVAILABLE_FOR_CHECKIN.replace(':customerId', customerId))
}

// set default card
const setDefaultCard = async (payload, customerId) => {
  return await httpClient.post(endpoints.CUSTOMERS.SAVE_CARD.replace(':customerId', customerId), payload)
}

export default {
  login,
  loginWithSecurityCode,
  getCustomerInfo,
  createAccount,
  resetPassword,
  sendVerificationCode,
  sendVerificationCodeEmail,
  verifyPhone,
  verifyEmail,
  updateCustomer,
  getAddresses,
  getFavoriteItems,
  addFavoriteItem,
  removeFavoriteItem,
  getSavedCards,
  getLoyaltyInfo,
  getOrders,
  saveAddress,
  updateAddress,
  deleteAddress,
  saveCard,
  deleteCard,
  changePassword,
  deleteCustomer,
  addFavoriteStore,
  removeFavoriteStore,
  getFavoriteStores,
  guestLogin,
  updateGuestCustomerInfo,
  logout,
  redeemLoyaltyInStore,
  collectLoyaltyInStore,
  foreignAuth,
  getGiftCards,
  checkinForOrder,
  getOrdersAvailableForCheckin,
  getSingleAddress,
  setDefaultCard,
}
